import type React from 'react';
import { useWizard, WizardStep } from '@zillow/zrs-rental-applications';
import {
  useHandleScreeningReports,
  useId,
  useIdVerificationCallbacks,
  usePassiveAuthData,
  usePidAnswers,
  useReauthenticating,
  useSetKiqQuestionSet,
  useSetLoading,
  useTermSet,
} from '../../contexts/IdVerificationContext';
import { Button, Flex, Grid, Heading, IconWarning, Label, Paragraph } from '@zillow/constellation';
import { ExitReason, IdvStep, LogLevel, TermSet } from '../../../../models';
import { LANDLORD_CLICKSTREAM_EVENTS, RENTER_CLICKSTREAM_EVENTS } from '../../clickstream/events';
import { useIdVerificationWebApiHandler_performIdentityCheck } from '@zg-rentals/ts-id-verification-client-react-query';
import { formatPhoneNumber } from '../../format/format';
import type { ButtonProps } from '../../NextButton/NextButton';
import { getZgAuthZoneFromTermSet } from '../../utils/zgAuthZoneUtils';
import {
  FROZEN_ACCOUNT_SUBSTEP,
  GENERIC_ERROR_SUBSTEP,
  USER_NOT_FOUND_LOCKED_OUT_SUBSTEP,
  USER_NOT_FOUND_SUBSTEP,
} from '../../constants/steps';
import { ErrorCodes } from '../../constants/errorCodes';
import { DatarouterError } from '@zg-rentals/web-base';
import { useEffectOnce } from 'react-use';
import { LANDLORD_METRIC_PREFIX, RENTER_METRIC_PREFIX } from '../../IdVerification';

interface ReviewStepProps {
  id: string;
  label: string;
}

const EMPTY_FIELD_TEXT = 'Not provided';

export function LabelDt(props: React.ComponentProps<typeof Label>) {
  // @ts-ignore
  return <Label as="dt" {...props} />;
}

export function ParagraphDd(props: React.ComponentProps<typeof Paragraph>) {
  return <Paragraph as="dd" {...props} />;
}

export default function ReviewStep({ id, label }: ReviewStepProps) {
  const pidAnswers = usePidAnswers();
  const { firstName, middleName, lastName, currentAddress, currentCity, currentState, currentZip, dob, phone, ssn } =
    pidAnswers;
  const { jsc, hdimPayload } = usePassiveAuthData();
  const termSet = useTermSet();
  const requestingClient = useId();
  const handleScreeningReports = useHandleScreeningReports();
  const { onKbaStepUp, onNotAuthenticated, onSubmitPidForm, trackEvent, emitMetric, log, onExit } =
    useIdVerificationCallbacks();
  const reauthenticating = useReauthenticating();
  const setLoading = useSetLoading();
  const setKiqQuestionSet = useSetKiqQuestionSet();
  const { onRequestStep, currentStep } = useWizard();

  useEffectOnce(() => {
    trackEvent(LANDLORD_CLICKSTREAM_EVENTS.viewPage('id_verification_review_personal_information'));
  });

  const { mutateAsync } = useIdVerificationWebApiHandler_performIdentityCheck({
    onMutate() {
      setLoading(true);
      const hasPassiveAuthData = hdimPayload && jsc;
      if (!hasPassiveAuthData) {
        if (termSet === TermSet.LandlordScreening) {
          emitMetric(`${LANDLORD_METRIC_PREFIX} missingJscOrHdim`);
        } else {
          emitMetric(`${RENTER_METRIC_PREFIX} missingJscOrHdim`);
        }
      }

      if (termSet === TermSet.LandlordScreening) {
        trackEvent(LANDLORD_CLICKSTREAM_EVENTS.clickNextSubmitIDV);
      } else if (ssn) {
        trackEvent(RENTER_CLICKSTREAM_EVENTS.submitIdvWithSsn);
      } else {
        trackEvent(RENTER_CLICKSTREAM_EVENTS.submitIdv);
      }
      onSubmitPidForm({
        ...pidAnswers,
        // Don't include sensitive information
        ssn: '',
        dob: '',
      });
    },
    onSuccess: async (data) => {
      if (data.passiveAuthSucceeded) {
        if (termSet === TermSet.LandlordScreening) {
          emitMetric(`${LANDLORD_METRIC_PREFIX} passiveAuth success`);
        } else {
          emitMetric(`${RENTER_METRIC_PREFIX} passiveAuth success`);
        }

        if (termSet === TermSet.LandlordScreening) {
          trackEvent(LANDLORD_CLICKSTREAM_EVENTS.idvSuccess);
        }
        await onExit({ reason: ExitReason.IdVerified, success: true });
      } else if (data.kiqEnabled && data.questionSet && data.questionSet.length > 0) {
        if (termSet === TermSet.LandlordScreening) {
          emitMetric(`${LANDLORD_METRIC_PREFIX} kbaStepUp`);
        } else {
          emitMetric(`${RENTER_METRIC_PREFIX} kbaStepUp`);
        }
        onKbaStepUp();
        const nextStep = currentStep === IdvStep.KiqOnboardingStep ? IdvStep.KiqStep : IdvStep.KiqOnboardingStep;
        setKiqQuestionSet(data.questionSet);
        onRequestStep(nextStep);
      } else {
        log(LogLevel.Error, {
          message: 'Error calling performPassiveIdentityCheck - no question set',
        });
        onRequestStep(IdvStep.ErrorStep, GENERIC_ERROR_SUBSTEP);
      }
    },
    onError(error) {
      if (termSet === TermSet.LandlordScreening) {
        emitMetric(`${LANDLORD_METRIC_PREFIX} performIdentityCheck error`);
      } else {
        emitMetric(`${RENTER_METRIC_PREFIX} performIdentityCheck error`);
      }
      if (DatarouterError.is(error)) {
        if (error.responseStatus === 401) {
          onNotAuthenticated();
          onExit({ reason: ExitReason.NotAuthenticated, success: false });
        } else {
          switch (error.code) {
            case ErrorCodes.USER_NOT_FOUND:
              onRequestStep(IdvStep.ErrorStep, USER_NOT_FOUND_SUBSTEP);
              break;
            case ErrorCodes.USER_NOT_FOUND_LOCKED_OUT:
              onRequestStep(IdvStep.ErrorStep, USER_NOT_FOUND_LOCKED_OUT_SUBSTEP);
              break;
            case ErrorCodes.FROZEN_CREDIT_REPORT:
              onRequestStep(IdvStep.ErrorStep, FROZEN_ACCOUNT_SUBSTEP);
              break;
            default:
              log(LogLevel.Error, {
                message: `Unexpected error code for performIdentityCheck: ${error.code}`,
                error,
              });
              onRequestStep(IdvStep.ErrorStep, GENERIC_ERROR_SUBSTEP);
              break;
          }
        }
      } else {
        log(LogLevel.Error, {
          message: 'Unexpected error response for performIdentityCheck',
          error,
        });
        onRequestStep(IdvStep.ErrorStep, GENERIC_ERROR_SUBSTEP);
      }
    },
    onSettled() {
      setLoading(false);
    },
  });

  return (
    <WizardStep
      id={id}
      label={label}
      meta={{
        renderNextButton: (buttonProps: ButtonProps) => (
          <Button {...buttonProps} buttonType="primary">
            Verify identity
          </Button>
        ),
      }}
      onSubmit={async () => {
        trackEvent(LANDLORD_CLICKSTREAM_EVENTS.clickNext('id_verification_review_personal_information'));
        if (!ssn && reauthenticating) {
          await onExit({ reason: ExitReason.AttemptedReAuthWithoutSsn, success: false });
          return;
        }
        await mutateAsync({
          postData: {
            ...pidAnswers,
            deviceType: 'desktop',
            requestingClient,
            zgAuthZone: getZgAuthZoneFromTermSet(termSet),
            isReAuthRequest: reauthenticating,
            hdimPayload,
            jsc,
          },
        });
      }}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onSuccess={(_: any, abortController: AbortController) => {
        abortController.abort();
      }}
      render={() => (
        <>
          <Grid
            as="dl"
            display="grid"
            gridRowGap="sm"
            gridTemplateColumns="max-content 1fr"
            gridTemplateAreas={
              termSet === TermSet.RenterScreening
                ? [
                    '. head',
                    '. subHead',
                    '. firstName',
                    'middleNameWarn middleName',
                    '. lastName',
                    '. dob',
                    '. phone',
                    'ssnWarn ssn',
                    '. address',
                  ]
                : [
                    '. head',
                    '. subHead',
                    '. firstName',
                    'middleNameWarn middleName',
                    '. lastName',
                    'ssnWarn ssn',
                    '. address',
                  ]
            }
          >
            <Grid gridArea="head">
              <Heading marginBottom="sm" level={4} as="h2">
                Review your personal information
              </Heading>
            </Grid>
            <Grid gridArea="subHead">
              <Paragraph marginBottom="sm">
                {termSet === TermSet.RenterScreening
                  ? 'The information you previously entered will be sent to Experian to verify your identity. If it’s incorrect, you may not be able to submit the application.'
                  : 'The information you previously entered will be used to verify your identity. If it’s incorrect, you may not be able to review your applicants’ information.'}
              </Paragraph>
            </Grid>
            <Grid gridArea="firstName">
              <Flex display="flex" flexDirection="column" gap="xs">
                <Flex display="flex" alignItems="flex-end">
                  <LabelDt>Legal first name</LabelDt>
                </Flex>
                <ParagraphDd>{firstName}</ParagraphDd>
              </Flex>
            </Grid>
            <Grid gridArea="middleNameWarn" marginRight="xs">
              {/* @ts-ignore */}
              {!middleName ? <IconWarning marginTop="xxs" fontColor="orange500" /> : null}
            </Grid>
            <Grid gridArea="middleName">
              <Flex display="flex" flexDirection="column" gap="xs">
                <LabelDt>Legal middle name</LabelDt>
                <ParagraphDd fontColor={middleName ? 'textDark' : 'textMedium'}>
                  {middleName ? middleName : EMPTY_FIELD_TEXT}
                </ParagraphDd>
                {!middleName && (
                  <Paragraph fontType="finePrint" fontColor="orange600">
                    If you have a middle name, you need to add it so that your identity can be verified
                  </Paragraph>
                )}
              </Flex>
            </Grid>
            <Grid gridArea="lastName">
              <Flex display="flex" flexDirection="column" gap="xs">
                <LabelDt>Legal last name</LabelDt>
                <ParagraphDd>{lastName}</ParagraphDd>
              </Flex>
            </Grid>
            {termSet === TermSet.RenterScreening && (
              <>
                <Grid gridArea="dob">
                  <Flex display="flex" flexDirection="column" gap="xs">
                    <LabelDt>Date of birth</LabelDt>
                    <ParagraphDd>{dob}</ParagraphDd>
                  </Flex>
                </Grid>
                <Grid gridArea="phone">
                  <Flex display="flex" flexDirection="column" gap="xs">
                    <LabelDt>Phone number</LabelDt>
                    <ParagraphDd>{formatPhoneNumber(phone)}</ParagraphDd>
                  </Flex>
                </Grid>
              </>
            )}
            <Grid gridArea="ssnWarn" marginRight="xs">
              {/* @ts-ignore */}
              {!ssn && handleScreeningReports ? <IconWarning marginTop="xxs" fontColor="orange500" /> : null}
            </Grid>
            <Grid gridArea="ssn">
              <Flex display="flex" flexDirection="column" gap="xs">
                <LabelDt>{termSet === TermSet.RenterScreening ? 'SSN or ITIN' : 'Social Security number'}</LabelDt>
                {ssn ? (
                  <ParagraphDd>XXX-XX-XXXX</ParagraphDd>
                ) : (
                  <>
                    <Paragraph fontColor="textMedium">{EMPTY_FIELD_TEXT}</Paragraph>
                    {handleScreeningReports && (
                      <Paragraph fontType="finePrint" fontColor="orange600">
                        {termSet === TermSet.RenterScreening
                          ? 'Add your SSN or ITIN to accurately verify your identity and pull the correct screening reports'
                          : 'If you have a Social Security number, you need to add it so that your identity can be verified'}
                      </Paragraph>
                    )}
                  </>
                )}
              </Flex>
            </Grid>
            <Grid gridArea="address">
              <Flex display="flex" flexDirection="column" gap="xs">
                <LabelDt>Current address</LabelDt>
                <Flex display="flex" flexDirection="column">
                  {/* We use a <br/> instead of 2 paragraphs because 'dd' and 'dt' tags should alternate with each other */}
                  <ParagraphDd>
                    {currentAddress}
                    <br />
                    {`${currentCity}, ${currentState} ${currentZip}`}
                  </ParagraphDd>
                </Flex>
              </Flex>
            </Grid>
          </Grid>
        </>
      )}
    />
  );
}
